import { render } from 'react-dom'
import './styles.css'
import * as THREE from 'three'
import { useRef, Suspense, useEffect, useState } from 'react'
import { Canvas, useFrame, useThree, extend } from '@react-three/fiber'
import { Center, Bounds, Image } from '@react-three/drei'
import * as Tone from 'tone'
import Colors from './Colors'
import Spring from './Spring'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass'
import * as f from 'fractal-noise'
import * as f2 from 'fast-simplex-noise'

function shuffle(array) {
  let currentIndex = array.length, randomIndex;
  while (currentIndex != 0) {
    randomIndex = Math.floor(fxrand() * currentIndex);
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]];
  }
  return array;
}

let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]
const ci = randI(Colors.length)
const bgColor = "#0a0a0a"
const cols = Colors[ci]
shuffle(cols)

let columns = rand([16,8,32]);
let rows = 1000;
let rect = f.makeRectangle(columns, rows, f2.makeNoise2D(fxrand), { frequency: (1 + randI(100)) * (0.0001), octaves: 4, persistence: randI(10) * 0.1 })
let bpm = 120
const feats = window.$fxhashFeatures = {
  phases: rand([3]),
  border: rand([2, 2, 3,3,4,4,5]),
  speed: rand([8]),
  camX: randI(3) * 0.08 * rand([1, -1]),
  camY: randI(3) * 0.04 * rand([1, -1]),
}
class AfterBurn extends AfterimagePass {
  constructor() {
    super(1)
    this.shaderMaterial.onBeforeCompile = (shader) => {
      shader.fragmentShader =
        `
        uniform float damp;
        uniform sampler2D tOld;
        uniform sampler2D tNew;
        varying vec2 vUv;
    
        vec4 when_gt( vec4 x, float y ) {
          return max( sign( x - y ), 0.0 );
        }

        void main() {
          vec4 texelOld = texture2D( tOld, vUv );
          vec4 texelNew = texture2D( tNew, vUv );
          texelOld *= damp * when_gt( texelOld, 0.00001 );
          gl_FragColor = max(texelNew, texelOld);
        }
        `
    }
  }
}

extend({ EffectComposer, RenderPass, AfterBurn })
const Tint = () => {
  const ref = useRef()
  const { size, gl, scene, camera } = useThree()
  const composer = useRef()
  useFrame(() => {
    ref.current.uniforms.damp.value = damp
    damp = defaultDamp
    composer.current.render()
  }, 1)
  return (
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachFns={['addPass']} args={[scene, camera]} />
      <afterBurn attachFns={['addPass']} args={[size]} ref={ref} />
    </effectComposer>
  )
}
export default function App() {
  const size = useWindowSize();
  useEffect(() => {
    posCount = 0
  }, [size.width])
  const start = () => {
    if (Tone.Transport.state !== 'started') {
      Tone.start();
      Tone.Transport.start();
      parts.forEach((p, i) => { p.start() })
      damp = 0
      defaultDamp = 1
      posCount = 0
      maxPosCount = 0
    }else{
      Tone.Transport.stop();
    }

  }
  return (
    <div id="main" onClick={() => start()} key={size.width}>
      <Canvas flat={true} dpr={[1, 2]} camera={{ near: 0.000001, far: 10000, position: [feats.camX, feats.camY, 7], fov: 170 }}>
        <Stuff />
        <Back />
        <Tint />
        <ambientLight intensity={0.5} />
      </Canvas>
    </div>
  )
}
function Stuff() {
  useFrame(() => {
    if (posCount < maxPosCount) {
      posCount+= 0.5;
    }
  })
  let rots = 3+randI(9)
  let rad = (Math.PI*2)/rots
  return (
    <group position={[0, -4, 0]} >
      <Bounds fit margin={0.05} damping={0}>
        <Center>
          {new Array(rots).fill().map((_,i) =>
            <group rotation={[0, 0, i*rad]} key={i}>
              {new Array(columns).fill().map((_, col) => new Array(1).fill().map((_, row) =>
                <Thang col={col} row={row} key={row + col} key={row} />
              ))}
            </group>
          )}
        </Center>
      </Bounds>
    </group>
  )
}

let posCount = 0
let spring = 1
let mode = rand([0,1,2,3,3,3,3,3])
let defaultDamp = 1
function Thang({ col, row }) {
  const ref = useRef();
  let getX = () => (col * 0.00005)
  let getY = () => -0.0008 + rect[col][posCount] * 0.002
  let getZ = () => (-row * 0.00008) - (posCount * 0.000006)
  useFrame(() => {
      ref.current.position.x = getX()
      ref.current.position.y = getY()
      ref.current.position.z = getZ()
      let m = Math.abs(modes[mode](0,col,posCount))
      ref.current.scale.setScalar(((s*0.9) + ((s*10)- (posCount*0.0000006)))*m)
      ref.current.material.zoom = 1.2 + (posCount * 0.01)
 
  })
  let s = 0.00001
  return (
      <Image ref={ref} ref={ref} url={'./spring'+spring+'.jpg'} scale={[s, s, s]} rotation={[0, 0, Math.PI * 0.25]} position={[getX(), 0, 0]} />
  )
}
function Back() {
  const ref = useRef();
  useFrame((state) => {
    if (state.clock.getElapsedTime() > 1) {
      ref.current.scale.setScalar(0)
    }
  })
  return (
      <group ref={ref}>
        <mesh>
          <meshBasicMaterial color={bgColor} side={THREE.BackSide} />
          <octahedronBufferGeometry args={[12, 3]} />
        </mesh>
      </group>
  )
}

Tone.Transport.bpm.value = bpm * feats.speed
var pianoNotesPool = ["C","Ds","Fs","A"];
let pianoSamples = {};
pianoNotesPool.map((val) => {
  for (var i = 3; i < 7; i++)
    pianoSamples[val.replace(/s/gi, '#') + i] = val + i + 'v' + rand([1, 1, 1, 1, 1, 1]) + '.[mp3|ogg]';
})
var pianoParams = [
  pianoSamples,
  {
    "release": 3,
    "attack": 0.05,
    "baseUrl": "./light/"
  }]

let pannerL = new Tone.Panner(0).toDestination()
let panner = new Tone.Panner(-0.6).toDestination()
let pannerR = new Tone.Panner(0.6).toDestination()
let sampler = new Tone.Sampler(...pianoParams)
let samplerL = new Tone.Sampler(...pianoParams)
let samplerR = new Tone.Sampler(...pianoParams)
sampler.connect(panner)
samplerR.connect(pannerR)
samplerL.connect(pannerL)

let parts = []
let damp = 0.5
Spring.forEach((notes, i) => {
  notes.forEach((note, j) => {
    Spring[i][j].time = Spring[i][j].time * feats.speed
    Spring[i][j].i = j
  })
})
let maxMax = 180
let maxPosCount = maxMax-1

new Array(feats.phases).fill().map((_, j) => {
  let t = Spring[randI(Spring.length)]
  let part = new Tone.Part(((time, { name, dur, vel,i }) => {
    rand([samplerR,samplerL, sampler]).triggerAttackRelease(name.replace(/s/gi, '#'), dur * feats.speed*4, time, (vel*0.5) + (rand([0.02,0.03,0.04])*2));
    maxPosCount+= 3;
    if(maxPosCount > maxMax){
        posCount = 0;
        maxPosCount = 0
        damp = 0
      }
  }), t)
  part.loop = true
  let n = t[randI(t.length-20)]
  part.loopStart = n.time
  part.loopEnd = n.time+rand([1,2]) + (rand([0.01,0.02,0.015])*rand([1,-1])*0.1)
  parts.push(part)
})
console.log('%c🎹🎹 @ramioso 2022-04-28 🎹🎹', 'color: orange');
let b = feats.border + 'vw'
let stylez = { background: bgColor, width: b, height: b }
document.getElementById('root').style.background = bgColor
render(
  <>
    <div id="borders">
      <div style={stylez} />
      <div style={stylez} />
      <div style={stylez} />
      <div style={stylez} />
    </div>
    <Suspense fallback={null}>
      <App />
    </Suspense>
  </>,
  document.getElementById('root')
)
function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowSize;
}

let modes = [
  (t, c, r) => Math.cos(t * 1 + Math.sqrt(c * r * 0.05)) + .5,
  (t, c, r) => Math.max(0, Math.sin(r ^ (c + 9 * t))),
  (t, c, r) => ((c + (r + t * 9) ^ c - (r + t * 9)) % 13 & 1) - .15,
  (t, c, r) => .02 / Math.sin(3 ** ++c - Math.atan2(c, c * r) * 3 - t),
]